import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Tooltip,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Badge,
  Divider,
  CircularProgress,
  Avatar
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import CategoryIcon from '@mui/icons-material/Category';
import MapIcon from '@mui/icons-material/Map';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Logo from './Logo';  // Import the Logo component
import NotificationCenter from './notifications/NotificationCenter';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import BarChartIcon from '@mui/icons-material/BarChart';
import InsightsIcon from '@mui/icons-material/Insights';
import HelpIcon from '@mui/icons-material/Help';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TuneIcon from '@mui/icons-material/Tune';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import ScienceIcon from '@mui/icons-material/Science';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useFeatureFlags, FEATURE_CODES } from '../context/FeatureFlagContext';
import { useAuth } from '../context/AuthContext';

const DRAWER_WIDTH = 240;
const COLLAPSED_DRAWER_WIDTH = 64;
const TOGGLE_WIDTH = 24;

const Navigation = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { isFeatureEnabled, isLoading, allFeatures } = useFeatureFlags();
  const [debugMode, setDebugMode] = useState(false);
  const { user, logout, refreshUserDetails } = useAuth();
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);

  useEffect(() => {
    // Log available features when they change
    if (allFeatures && Object.keys(allFeatures).length > 0) {
      console.log('Available features for navigation:', allFeatures);
    }
  }, [allFeatures]);

  // Refresh user details when the component mounts
  useEffect(() => {
    const loadUserDetails = async () => {
      if (user && user.user_id && !userDetailsLoading) {
        setUserDetailsLoading(true);
        try {
          await refreshUserDetails();
        } catch (error) {
          console.error('Error refreshing user details:', error);
        } finally {
          setUserDetailsLoading(false);
        }
      }
    };
    
    // Only run once when component mounts
    loadUserDetails();
  }, []); // Empty dependency array to run only once on mount

  // Update the isSelected function to be more precise
  const isSelected = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Exact match for inventory to avoid matching with inventory-optimization
    if (path === '/inventory') {
      return location.pathname === '/inventory';
    }
    // For other paths, keep the startsWith behavior
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setAnchorEl(null);
  };

  // Navigation items with their feature codes
  const navItems = [
    { 
      path: '/', 
      name: 'Dashboard', 
      icon: <DashboardIcon />, 
      featureCode: FEATURE_CODES.PRODUCT // Dashboard is available with product module
    },
    { 
      path: '/product-master', 
      name: 'Product Master', 
      icon: <CategoryIcon />, 
      featureCode: FEATURE_CODES.PRODUCT 
    },
    { 
      path: '/product-hierarchy', 
      name: 'Product Hierarchy', 
      icon: <AccountTreeIcon />, 
      featureCode: FEATURE_CODES.PRODUCT 
    },
    { 
      path: '/product-supplier', 
      name: 'Product Suppliers', 
      icon: <CategoryIcon />, 
      featureCode: FEATURE_CODES.PRODUCT 
    },
    { 
      path: '/inventory-hierarchy', 
      name: 'Inventory Hierarchy', 
      icon: <StorageIcon />, 
      featureCode: FEATURE_CODES.INVENTORY 
    },
    { 
      path: '/inventory', 
      name: 'Inventory Details', 
      icon: <InventoryIcon />, 
      featureCode: FEATURE_CODES.INVENTORY 
    },
    { 
      path: '/forecast-runs', 
      name: 'Forecast Runs', 
      icon: <AssessmentIcon />, 
      featureCode: FEATURE_CODES.FORECASTING 
    },
    { 
      path: '/install-base/', 
      name: 'Install Base', 
      icon: <BuildIcon />, 
      featureCode: 'FLAG_INSTALL_BASE_MODULE_001' 
    },
    { 
      path: '/service-requests', 
      name: 'Service Requests', 
      icon: <HelpIcon />, 
      featureCode: 'FLAG_INSTALL_BASE_MODULE_001' // Show with install base module
    },
    { 
      path: '/inventory-optimization', 
      name: 'Inventory Optimization', 
      icon: <TuneIcon />, 
      featureCode: FEATURE_CODES.OPTIMIZATION 
    },
    { 
      path: '/orders', 
      name: 'Buying Desk', 
      icon: <ShoppingCartIcon />, 
      featureCode: FEATURE_CODES.ORDER 
    },
    { 
      path: '/network', 
      name: 'Network Map', 
      icon: <MapIcon />, 
      featureCode: FEATURE_CODES.NETWORK 
    },
    { 
      path: '/simulation', 
      name: 'Simulation', 
      icon: <ScienceIcon />, 
      featureCode: FEATURE_CODES.NETWORK 
    },
    { 
      path: '/shipment-tracking', 
      name: 'Shipment Tracking', 
      icon: <LocalShippingIcon />, 
      featureCode: FEATURE_CODES.SHIPMENT 
    },
    { 
      path: '/config', 
      name: 'Configuration', 
      icon: <SettingsIcon />, 
      featureCode: FEATURE_CODES.CONFIG 
    },
    { 
      path: '/invoice-processing', 
      name: 'Invoice Processing', 
      icon: <ReceiptIcon />, 
      featureCode: FEATURE_CODES.INVOICE_PROCESSING 
    },
    { 
      path: '/developers', 
      name: 'Developer Tools', 
      icon: <CodeIcon />, 
      featureCode: FEATURE_CODES.DEVELOPER 
    },
    { 
      path: '/developers/user-management', 
      name: 'User Management', 
      icon: <PeopleIcon />, 
      featureCode: FEATURE_CODES.DEVELOPER 
    }
  ];

  // Filter navigation items based on feature flags
  const filteredNavItems = useMemo(() => {
    return navItems.filter(item => {
      const enabled = isFeatureEnabled(item.featureCode);
      console.log(`Navigation item ${item.name} (${item.featureCode}): ${enabled ? 'ENABLED' : 'DISABLED'}`);
      return enabled || debugMode;
    });
  }, [navItems, isFeatureEnabled, debugMode]);

  // Toggle debug mode with Ctrl+Shift+D
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setDebugMode(prev => !prev);
        console.log('Debug mode:', !debugMode);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [debugMode]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleClose();
  };

  const bottomItems = [
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Help', icon: <HelpIcon />, path: '/help' },
  ];

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={() => setIsExpanded(!isExpanded)}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              mr: 2,
              filter: 'brightness(0) invert(1)', // Convert to white
              display: 'flex',
              alignItems: 'center'
            }}>
              <Logo variant="default" />
            </Box>
            <Typography variant="h6" noWrap component="div">
              {process.env.REACT_APP_APP_NAME}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationCenter />
            
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ mr: 1 }}>
                  {user.full_name || user.username || 'User'}
                </Typography>
                
                <IconButton 
                  onClick={handleClick}
                  color="inherit"
                  size="large"
                  edge="end"
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  {userDetailsLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      {(user.full_name || user.username || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                  )}
                </IconButton>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiMenuItem-root': {
              minWidth: 200,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {user && (
          <>
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </MenuItem>
            {user.roles && user.roles.length > 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  Roles: {user.roles.join(', ')}
                </Typography>
              </MenuItem>
            )}
            <Divider />
          </>
        )}
        <MenuItem onClick={handleClose}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Box sx={{ display: 'flex' }}>
        <Drawer
          variant="permanent"
          sx={{
            width: isExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            transition: 'width 0.3s ease',
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: isExpanded ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
              transition: 'width 0.3s ease',
              boxSizing: 'border-box',
              backgroundColor: '#1A2942',
              color: 'white',
              borderRight: 'none',
              overflowX: 'hidden',
              paddingTop: '64px'
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              
            </Typography>
          </Box>
          <Divider />
          <List>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredNavItems.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption">No modules available</Typography>
              </Box>
            ) : (
              filteredNavItems.map((item) => (
                <Tooltip 
                  key={item.path} 
                  title={!isExpanded ? item.name : ''} 
                  placement="right"
                >
                  <ListItem
                    button 
                    onClick={() => handleNavigation(item.path)}
                    selected={location.pathname === item.path}
                    sx={{
                      minHeight: 48,
                      px: 2.5,
                      backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.15) !important' : 'transparent',
                      borderRight: location.pathname === item.path ? '3px solid #00A3E0 !important' : 'none',
                      '&:hover': {
                        backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 0, 
                        mr: isExpanded ? 2 : 'auto', 
                        justifyContent: 'center',
                        color: location.pathname === item.path ? 'white' : 'rgba(255,255,255,0.7)',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {isExpanded && (
                      <ListItemText 
                        primary={item.name} 
                        sx={{ 
                          color: location.pathname === item.path ? 'white' : 'rgba(255,255,255,0.7)',
                        }} 
                      />
                    )}
                  </ListItem>
                </Tooltip>
              ))
            )}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <List>
            {bottomItems.map((item) => (
              <ListItem 
                button 
                key={item.text} 
                component={RouterLink} 
                to={item.path}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Drawer>

        {/* Separate Toggle Column */}
        <Box
          sx={{
            width: TOGGLE_WIDTH,
            backgroundColor: 'transparent',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            mt: '64px',
          }}
        >
          <IconButton
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              position: 'fixed',
              left: isExpanded ? DRAWER_WIDTH - (TOGGLE_WIDTH/2) : COLLAPSED_DRAWER_WIDTH - (TOGGLE_WIDTH/2),
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: '#1A2942',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              width: TOGGLE_WIDTH,
              height: TOGGLE_WIDTH,
              '&:hover': {
                backgroundColor: '#2A3952',
                border: '1px solid rgba(255,255,255,0.5)',
              },
              transition: 'left 0.3s ease',
              zIndex: 1300,
            }}
          >
            {isExpanded ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
      </Box>
    </>
  );
};

export default Navigation; 